import { getMembershipImportDate } from '../../src/site.config.js'
import DateTime from "../../lib/dateTime.js"

export default class UserView {
	id = null
	createdOn = null
	number = null
	name = null
	email = null
	emailBounced = false
	emailComplained = false
	avatar = null
	hasSetAvatar = false
	isAdmin = false
	bornOn = null
	usingGoogleWallet = false

	// The Ids of all the clubs to NOT be subscribed to
	clubUnsubscriptions = []
	// The Ids of all the clubs this user is an admin of
	clubsAdmin = []

	isAdminOfClub(clubId)
	{
		return this.clubsAdmin.indexOf(clubId) > -1;
	}

	vendorName = null;
	get isVendor()
	{
		return this.vendorName != null;
	}

	certification = new UserView.Certification();
	rso = new UserView.RSO();
	map = new UserView.MAP();
	membership = new UserView.Membership();
	insurance = new UserView.Insurance();
	licensing = new UserView.Licensing();
	dbs = new UserView.DBS();
	ukra = new UserView.UKRA();
	tps = new UserView.TPS();
	
	allRecords = []

	get records()
	{
		let records = [...this.allRecords]
		records.sort((a, b) => a.class.charCodeAt(0) - b.class.charCodeAt(0))
		// TODO: separate by type
		//records = distinct(records, r => r.recordClass);
		return records;
	}

	get isComplete()
	{
		// Keep in sync with userLite
		return this.bornOn != null
				&& this.hasSetAvatar;
	}
	
	get wasImported()
	{
		return this.createdOn < getMembershipImportDate();
	}

	get age()
	{
		if(!this.bornOn)
			return null;

		var today = DateTime.today;
		var age = today.year - this.bornOn.year;
		var m = today.month - this.bornOn.month;
		if (m < 0 || (m === 0 && today.day < this.bornOn.day))
			age--;
		
		return age;	
	}

	get eligibleForCertification()
	{
		return this.age >= 12;
	}

	get birthday()
	{
		// Hack for imported people, some suffer a timezoning issue where BST was not properly converted to UTC
		if(this.bornOn?.hours == 23)
			return this.bornOn.addHours(1).format('MM-dd');
		
		return this.bornOn?.format('MM-dd')
	}

	get dbsSummary()
	{
		let result = { };

		result.rso = !!this.rso.level;
		result.mapa = this.map.assessorStatus == 'approved' || this.map.assessorStatus == 'passed'

		// TODO: Club runner
		result.required = result.rso || result.mapa;
		result.status = this.dbs.status;
		result.outstanding = result.required && result.status != 'passed';

		return result;
	}

	static Certification = class {
		level1 = new UserView.Certification.Level1()
		level2 = new UserView.Certification.Level2()
		level3 = new UserView.Certification.Level3()

		get nextStage()
		{
			return this.level1.nextStage
				|| this.level2.nextStage 
				|| this.level3.nextStage
		}

		get level()
		{
			if(this.level3.status == 'passed')
				return 'level3';

			if(this.level2.status == 'passed')
				return 'level2';
			
			if(this.level1.status == 'passed')
				return 'level1';
			
			return null;
		}

		get levelNumber()
		{
			if(this.level3.status == 'passed')
				return 3;

			if(this.level2.status == 'passed')
				return 2;
			
			if(this.level1.status == 'passed')
				return 1;
			
			return 0;
		}

		getAttempt(id) {
			let attempt = this.level1.stage1.attempts.find(a => a.id == id);
			if(attempt)
				return attempt;

			attempt = this.level1.stage2.attempts.find(a => a.id == id);
			if(attempt)
				return attempt;

			attempt = this.level1.stage3.attempts.find(a => a.id == id);
			if(attempt)
				return attempt;

			attempt = this.level2.stage1.attempts.find(a => a.id == id);
			if(attempt)
				return attempt;

			attempt = this.level2.stage2.attempts.find(a => a.id == id);
			if(attempt)
				return attempt;

			attempt = this.level3.stage1.attempts.find(a => a.id == id);
			if(attempt)
				return attempt;

			return null;
		}

		pushAttempt(attempt)
		{
			switch(attempt.type)
			{
				case "L1S1":
					this.level1.stage1.attempts.push(attempt)
					break;
				case "L1S2":
					this.level1.stage2.attempts.push(attempt)
					break;
				case "L1S3":
					this.level1.stage3.attempts.push(attempt)
					break;
				case "L2S1":
					this.level2.stage1.attempts.push(attempt)
					break;
				case "L2S2":
					this.level2.stage2.attempts.push(attempt)
					break;
				case "L3S1":
					this.level3.stage1.attempts.push(attempt)
					break;
			}
		}

		get attemptPending()
		{
			return this.level1.attemptPending ||
					this.level2.attemptPending ||
					this.level3.attemptPending
		}

		static Attempt = class {
			id = null
			submittedOn = null

			type = null
			rocketId = null
			flightId = null
			
			processedOn = null
			processedByUserId = null
			result = null
			reason = null
		}

		static Stage = class {
			attempts = []
			grantedOn = null

			get status()
			{
				if(this.grantedOn != null)
					return 'passed';

				if(this.attempts.length == 0)
					return 'not-started';
				
				if(this.attempts[this.attempts.length - 1].result == null)
					return 'pending'

				if(this.attempts[this.attempts.length - 1].result == 'passed')
					return 'passed'
				
				return 'failed';
			}

			get passedOn()
			{
				if(!this.attempts.length)
					return null;

				if(this.attempts[this.attempts.length - 1].result == 'passed')
					return this.attempts[this.attempts.length - 1].submittedOn;

				return null;
			}
		}

		static Level1 = class {
			grantedOn = null;

			stage1 = new UserView.Certification.Stage()
			stage2 = new UserView.Certification.Stage()
			stage3 = new UserView.Certification.Stage()

			get attemptPending()
			{
				return this.stage1.status == 'pending' 
					|| this.stage2.status == 'pending' 
					|| this.stage3.status == 'pending';
			}

			get status()
			{
				if(this.grantedOn)
					return 'passed';

				if(this.stage1.status == 'not-started')
					return 'not-started';

				if(this.stage3.status == 'passed')
					return 'passed';

				return 'in-progress';
			}

			get nextStage()
			{
				if(this.grantedOn)
					return null;

				if(this.stage1.status != 'passed')
					return 'L1S1';

				if(this.stage2.status != 'passed')
					return 'L1S2';

				if(this.stage3.status != 'passed')
					return 'L1S3';

				return null;
			}

			get passedOn()
			{
				if(this.status == 'passed')
				{
					if(this.grantedOn)
						return this.grantedOn;

					return this.stage3.passedOn
				}
			}

			get tasksComplete()
			{
				if(this.status == 'passed')
					return '3/3';

				if(this.stage1.status != 'passed')
					return '0/3';

				if(this.stage2.status != 'passed')
					return '1/3';

				if(this.stage3.status != 'passed')
					return '2/3';
				
				return '3/3';
			}

			get percentComplete()
			{
				if(this.status == 'passed')
					return 100;

				if(this.stage1.status != 'passed')
					return 0;

				if(this.stage2.status != 'passed')
					return 33;

				if(this.stage3.status != 'passed')
					return 66;
				
				return 100;
			}
		}

		static Level2 = class {
			grantedOn = null
			stage1 = new UserView.Certification.Stage()
			stage2 = new UserView.Certification.Stage()

			get attemptPending()
			{
				return this.stage1.status == 'pending' 
					|| this.stage2.status == 'pending';
			}

			get status()
			{
				if(this.grantedOn)
					return 'passed';
				
				if(this.stage1.status == 'not-started')
					return 'not-started';

				if(this.stage2.status == 'passed')
					return 'passed';

				return 'in-progress';
			}

			get nextStage()
			{
				if(this.grantedOn)
					return null;

				if(this.stage1.status != 'passed')
					return 'L2S1';

				if(this.stage2.status != 'passed')
					return 'L2S2';

				return null;
			}
			
			get passedOn()
			{
				if(this.status == 'passed')
				{
					if(this.grantedOn)
						return this.grantedOn;

					return this.stage2.passedOn
				}
			}
			
			get tasksComplete()
			{
				// account for Granted
				if(this.status == 'passed')
					return '2/2';

				if(this.stage1.status != 'passed')
					return '0/2';

				if(this.stage2.status != 'passed')
					return '1/2';
				
				return '2/2';
			}

			get percentComplete()
			{
				// account for Granted
				if(this.status == 'passed')
					return 100;

				if(this.stage1.status != 'passed')
					return 0;

				if(this.stage2.status != 'passed')
					return 50;
				
				return 100;
			}
		}

		static Level3 = class {
			grantedOn = null
			stage1 = new UserView.Certification.Stage()

			get attemptPending()
			{
				return this.stage1.status == 'pending';
			}

			get status()
			{
				if(this.grantedOn)
					return 'passed';

				if(this.stage1.status == 'not-started')
					return 'not-started';

				if(this.stage1.status == 'passed')
					return 'passed';

				return 'in-progress';
			}

			get nextStage()
			{
				if(this.grantedOn)
					return null;

				if(this.stage1.status != 'passed')
					return 'L3S1';

				return null;
			}

			get passedOn()
			{
				if(this.status == 'passed')
				{
					if(this.grantedOn)
						return this.grantedOn;

					return this.stage1.passedOn;
				}
			}
			
			get tasksComplete()
			{
				if(this.status == 'passed')
					return '1/1';

				if(this.stage1.status != 'passed')
					return '0/1';

				return '1/1';
			}

			get percentComplete()
			{
				// account for Granted
				if(this.status == 'passed')
					return 100;
				
				if(this.stage1.status != 'passed')
					return 0;

				return 100;
			}
		}
	}

	static RSO = class {
		mode = null;
		model = new UserView.RSO.Level()
		level1 = new UserView.RSO.Level()
		level2 = new UserView.RSO.Level()
		level3 = new UserView.RSO.Level()

		get level()
		{
			if(this.level3.status == 'passed')
				return 'level3';

			if(this.level2.status == 'passed')
				return 'level2';
			
			if(this.level1.status == 'passed')
					return 'level1';

			if(this.model.status == 'passed')
				return 'model';
			
			return null;
		}

		get status()
		{
			if(this.level3.status == 'passed')
				return 'passed';

			if(this.level2.status == 'passed')
				return 'passed';
			
			if(this.level1.status == 'passed')
					return 'passed';

			if(this.model.status == 'passed')
				return 'passed';
			
			return null;
		}
		
		get grantedOn()
		{
			if(this.level3.status == 'passed')
				return this.level3.grantedOn;

			if(this.level2.status == 'passed')
				return this.level2.grantedOn;
			
			if(this.level1.status == 'passed')
				return this.level1.grantedOn;

			if(this.model.status == 'passed')
				return this.model.grantedOn;
			
			return null;
		}
		
		static Level = class {
			grantedOn = null
			grantType = null
			appliedOn = null

			get status()
			{
				if(this.grantedOn)
					return 'passed';

				if(this.appliedOn)
					return 'in-progress'

				return 'not-started';
			}
		}
	}

	static MAP = class {
		get isAssessor()
		{
			return this.assessorStatus == 'passed'
		}
		
		assessorStatus = 'none'
		assessorGrantedOn = null
		// This property is used in a Migration inside Mapping.js.
		// Adjust the migration if you remove/rename this
		attemptPending = false
		completedTasks = []
		badges = []
		hydrazine = 0
		xp = 0
		level = 'recruit'
		levelChangedOn = null

		hasCompletedTask(taskId)
		{
			return this.completedTasks.indexOf(taskId) > -1
		}
	}

	static Record = class {
		flownOn = null
		location = null
		class = null
		type = null
		altitude = null
		rocketId = null
		flightId = null
		status = null
	}

	static Membership = class {
		number = null
		startedOn = null
		expiresOn = null

		get status()
		{
			if(this.startedOn == null)
				return 'non-member'
			
			if(this.expiresOn != null && this.expiresOn < DateTime.now)
				return 'expired'

			return 'active';
		}
	}

	static Insurance = class {
		status = 'unknown'
		BMFANumber = null
		BMFASurname = null
	}

	static Licensing = class {
		storeLimit = 5000
		singleMotorLimit = true
	}

	static DBS = class {
		status = 'none'
	}
	
	static UKRA = class {
		role = null
	}
	
	static TPS = class {
		status = "none"
	}
}